import { useFloating, autoUpdate, size, useInteractions, useRole, useDismiss, useListNavigation, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import { useClassNames, useBoolean } from '@magnetic/hooks';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { InputFrame } from '../input-frame.js';
import { InputWithAnalytics } from '../input-with-analytics.js';
import { Categories } from './categories.js';
import { filterCategories } from './filter-categories.js';
import { filterOptions } from './filter-options.js';
import { findCategoryIndex } from './find-category-index.js';
import { isAutocompleteCategories } from './is-autocomplete-categories.js';
import { Options } from './options.js';
import { setValue } from './set-value.js';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const InputAutocomplete = /* @__PURE__ */ __name(({
  className,
  disabled,
  fixedWidth,
  hiddenLabel,
  invalid = false,
  label,
  maxShownCategories = 2,
  maxShownOptions = 6,
  onChange,
  onClear,
  optional,
  options,
  prefix,
  required = false,
  size: size$1 = "md",
  suffix,
  tooltip,
  value,
  ...rest
}) => {
  const menuClassName = useClassNames(
    "mds-rebuild-input-autocomplete-menu",
    `mds-rebuild-input-autocomplete-menu-size-${size$1}`
  );
  const { setValue: setOpen, value: open } = useBoolean(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const listRef = useRef([]);
  const { context, refs, strategy, x, y } = useFloating({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      size({
        apply: ({ availableHeight, elements, rects }) => {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`
          });
        }
      })
    ]
  });
  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions(
    [
      useRole(context, { role: "listbox" }),
      useDismiss(context),
      useListNavigation(context, {
        listRef,
        activeIndex,
        onNavigate: setActiveIndex,
        virtual: true,
        loop: true
      })
    ]
  );
  const handleChange = useCallback(
    (event) => {
      const { value: value2 } = event.target;
      onChange == null ? void 0 : onChange(event);
      if (value2) {
        setOpen(true);
        setActiveIndex(0);
      } else {
        setOpen(false);
      }
    },
    [onChange, setOpen]
  );
  const filteredOptions = useMemo(() => {
    if (!value) {
      return options;
    }
    if (isAutocompleteCategories(options)) {
      return filterCategories(
        options,
        value,
        maxShownOptions,
        maxShownCategories
      );
    }
    return filterOptions(options, value, maxShownOptions);
  }, [value, options, maxShownOptions, maxShownCategories]);
  const onKeyDown = useCallback(
    (event) => {
      const value2 = isAutocompleteCategories(filteredOptions) ? findCategoryIndex(filteredOptions, activeIndex != null ? activeIndex : 0) : filteredOptions[activeIndex != null ? activeIndex : 0];
      if (event.key === "Enter" && activeIndex !== null && value2 && refs.reference.current) {
        setValue(refs.reference.current, value2);
        setActiveIndex(null);
        setOpen(false);
      }
    },
    [refs.reference, setOpen, activeIndex, filteredOptions]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(InputFrame, {
    className,
    disabled,
    fixedWidth,
    hasValue: !!value,
    hiddenLabel,
    invalid,
    label,
    onClear,
    optional,
    prefix,
    ref: refs.setReference,
    required,
    size: size$1,
    suffix,
    tooltip
  }, /* @__PURE__ */ React.createElement(InputWithAnalytics, {
    "aria-invalid": invalid,
    label,
    ...getReferenceProps({
      ...rest,
      disabled,
      required,
      type: "search",
      ref: refs.setReference,
      onChange: handleChange,
      value,
      "aria-autocomplete": "list",
      onKeyDown
    })
  })), /* @__PURE__ */ React.createElement(FloatingPortal, null, open && filteredOptions.length > 0 && /* @__PURE__ */ React.createElement(FloatingFocusManager, {
    context,
    initialFocus: -1,
    visuallyHiddenDismiss: true
  }, /* @__PURE__ */ React.createElement("div", {
    ...getFloatingProps({
      ref: refs.setFloating,
      className: menuClassName,
      style: {
        position: strategy,
        left: x != null ? x : 0,
        top: y != null ? y : 0
      }
    })
  }, isAutocompleteCategories(filteredOptions) ? /* @__PURE__ */ React.createElement(Categories, {
    activeIndex,
    getItemProps,
    listRef,
    options: filteredOptions,
    refs,
    setOpen,
    value
  }) : /* @__PURE__ */ React.createElement(Options, {
    activeIndex,
    getItemProps,
    listRef,
    options: filteredOptions,
    refs,
    setOpen,
    value
  })))));
}, "InputAutocomplete");
InputAutocomplete.displayName = "Input.Autocomplete";

export { InputAutocomplete };
//# sourceMappingURL=index.js.map
